/**
 * Graph workflow UI utilities
 * 
 * All graph definitions come from the backend API.
 * This file contains only conversion utilities.
 */

import { getLayoutedElements } from '../utils/layout';
import { dispatchedSlugsOf, dispatchMemberId } from '../../../utils/dispatchMarker';
import { nodeRunsModel } from '../../../utils/nodeModel';

// Room a node's MODEL DISC needs. It sits below-right of its node, so both the
// sibling gap (disc + caption hang ~72px below the node's own caption) and the
// layer channel (the 76px-wide disc block has to fit beside a 124px caption)
// grow. Applied ONLY when the graph actually carries a disc, so every other
// graph lays out byte-identically.
const MODEL_DISC_SIBLING_SPACING = 110;
const MODEL_DISC_LAYER_SPACING = 210;

/**
 * Resolve the caption shown under a node's name on the graph chart.
 *
 * Priority:
 *   1. An explicit, human-authored `description` on the node config (the field
 *      template authors add — `addNode(id, { description: '…' })`). This is the
 *      clean, primary source — shown verbatim (the CSS clamps it to one line).
 *   2. Fallback: a CLEAN short summary derived from the node's `prompt`. Deployed
 *      pipelines were serialized BEFORE descriptions existed, so they have no
 *      `description` and fall back here. A raw system prompt ("You are an expert
 *      code reviewer continuing a…") is ugly, so we strip the role-preamble
 *      boilerplate, take a concise first clause, and hard-cap ~80 chars. If
 *      nothing clean survives, we show NOTHING rather than an ugly dump.
 *   3. Nothing (null) when neither exists (Start/End/pure Condition w/o prompt).
 *
 * Returns `{ caption, full }`: `caption` is the short text shown under the name;
 * `full` is the complete description (NOT the raw prompt) for the hover tooltip
 * — falling back to the prompt would just put the ugly dump in the tooltip.
 */

// Leading "role preamble" boilerplate common to system prompts. We strip these
// so the caption starts at the substantive instruction, not "You are a/an …".
const ROLE_PREAMBLE_RE = /^\s*(you\s+are\s+(?:a|an|the)?\s*[^.!?\n]*?[.!?]\s*)+/i;
// A prompt-derived summary is only worth showing if it reads like a real,
// human-legible clause: enough words, not mostly markup/symbols.
const looksLikeCleanSummary = (s) => {
  if (!s) return false;
  const words = s.split(/\s+/).filter(Boolean);
  if (words.length < 3) return false; // too terse to be meaningful
  const letters = (s.match(/[a-zA-Z]/g) || []).length;
  return letters / s.length > 0.55; // mostly prose, not code/markup
};

export function deriveNodeCaption(nodeConfig) {
  if (!nodeConfig || typeof nodeConfig !== 'object') return { caption: null, full: null };

  const description = typeof nodeConfig.description === 'string' ? nodeConfig.description.trim() : '';
  if (description) {
    return { caption: description, full: description };
  }

  const prompt = typeof nodeConfig.prompt === 'string' ? nodeConfig.prompt.trim() : '';
  if (prompt) {
    // 1) Drop a leading "You are a/an … ." role preamble (one or more sentences).
    let body = prompt.replace(ROLE_PREAMBLE_RE, '').trim();
    if (!body) body = prompt; // preamble WAS the whole prompt — nothing better

    // 2) Take the first clause/sentence (stop at . ! ? or newline).
    const firstSentenceMatch = body.match(/^[^.!?\n]*[.!?]?/);
    let summary = (firstSentenceMatch ? firstSentenceMatch[0] : body).trim();
    if (!summary) summary = body.split('\n')[0].trim();

    // 3) If what survives isn't a clean, legible clause (e.g. the prompt is one
    //    giant role definition with no good short summary), show NOTHING rather
    //    than an ugly truncated dump. Explicit `description` is the clean path.
    if (!looksLikeCleanSummary(summary)) {
      return { caption: null, full: null };
    }

    // 4) Hard-cap ~80 chars.
    if (summary.length > 80) {
      summary = `${summary.slice(0, 80).trimEnd()}…`;
    }
    // Tooltip text = the cleaned summary (no trailing ellipsis), NOT the raw
    // prompt — we never want the full system-prompt dump in the tooltip either.
    const full = summary.replace(/…$/, '').trim();
    return { caption: summary, full };
  }

  return { caption: null, full: null };
}

// ONE picture for a conditional fan-out, whoever forks.
//
// The engine draws a fork one of two ways (agent-workflow graph.ts
// serialize()): an AUTHORED router node — no work payload, e.g. sentry-triage's
// `route` / `has_issues` — IS the branch point and fans out from itself, while a
// WORKING node (code/agent/dispatch, e.g. board-runner's `intake` / `poll`) gets
// a display-only `<node>__branch` decision node materialized after it. Same
// topology either way (work box → decision box → labelled branches); only the
// caption differed.
//
// This viewer used to drop `data.label` for EVERY decision node, so an authored
// router lost the name its template gave it and rendered as the same anonymous
// "Condition" card as the synthesized furniture — information thrown away, and
// out of step with the self-host dashboard, which prints the label. Now: the
// declared name survives, the synthesized id never reaches the picture.
//
// The `<node>__branch` id itself is load-bearing and untouched (PipelineDetailPage
// filters it out of the run step list; the engine's tests pin it). Deployed rows
// carry a FROZEN serialized graph, so normalizing here — not in the engine — is
// what reaches an already-deployed agent.
//
// TWO PLACES, one contract — the self-host twin is `selfhosted/ui/index.html`
// (BRANCH_NODE_LABEL / nodeBoxCaption). Tripwires, one per repo: this repo's
// `graphs.test.js` and `selfhosted/__tests__/ui-graph-branch-label.test.js`.
export const BRANCH_NODE_LABEL = 'Condition';
export const isSyntheticBranchId = (id) => /__branch$/.test(String(id ?? ''));
export const decisionNodeLabel = (nodeDef) => (
  isSyntheticBranchId(nodeDef?.id)
    ? BRANCH_NODE_LABEL
    : (nodeDef?.data?.label || BRANCH_NODE_LABEL)
);

// ── THE TEAM, DRAWN — a dispatching node fans out to the agents it starts ────
//
// A node that dispatches child agents declares them, and only them, in
// `nodeConfigs[id].dispatchesWorkflow` (string, or an array for a ROSTER node
// like board-runner's `dispatch`, which starts six). Until now the picture said
// so with a CHIP on the node — "⧉ 6 agents" — which names a count, not a team:
// the one question a fleet's graph has to answer (who does this manager
// command?) was answered by a tooltip.
//
// So each declared child gets a node of its own, joined to its dispatcher by a
// line: one manager, N members, 1→N. It reuses the ordinary node visual — no
// new node type, no new styling — because a child agent IS a box on this
// canvas; it simply belongs to another graph.
//
// WHY HERE AND NOT IN THE ENGINE'S `toJSON`. Both were on the table:
//
//  1. REACH. A deployed row and a marketplace row each carry a FROZEN
//     serialized graph, stamped at deploy/sync time. A serializer change
//     reaches NOTHING until agent-workflow ships, the templates re-pin, the
//     marketplace re-syncs and every deployed agent is redeployed. Deriving it
//     in the viewer draws the fan-out on the graph the box already holds,
//     today. This is the same argument decisionNodeLabel above records for the
//     `__branch` normalization, and it is the decisive one.
//  2. POLLUTION. `nodes[]` is not just a picture: `deriveComposedOf`, the
//     deploy cascade, the run step list and PipelineDetailPage's step
//     attribution all read it. Display-only members in there would need
//     filtering out at every one of those — N special cases instead of none.
//  3. THE FLEET CONCEPT STAYS OUT OF THE ENGINE (founder ruling). The engine
//     keeps carrying a marker; expanding it into pictures is a viewer's job.
//
// The cost is the honest one: the self-host dashboard holds its own copy
// (`dispatchMemberNodes` in selfhosted/ui/index.html) because the two UIs share
// ZERO code — the same already-paid cost as START/END/branch/caption rendering,
// and each side carries its own tripwire (here: graphs.test.js; there:
// selfhosted/__tests__/ui-graph-dispatch-fanout.test.js).
/**
 * One React Flow node per declared child agent, plus the edge that joins it to
 * its dispatcher. Pure; returns empty arrays when nothing dispatches.
 *
 * The member node carries `data.dispatchesWorkflow = [slug]` — the SAME shape a
 * real dispatch node carries — so PipelineGraphView's existing readiness/click
 * wiring treats it exactly like one: red when that child cannot run, click goes
 * to that child's own page. No new branch anywhere.
 *
 * @param {object[]} rfNodes  the converted React Flow nodes
 * @param {object} nodeConfigs  the serialized graph's nodeConfigs
 * @returns {{nodes: object[], edges: object[]}}
 */
export function dispatchMemberNodes(rfNodes, nodeConfigs) {
  const nodes = [];
  const edges = [];
  const taken = new Set((rfNodes || []).map((n) => n?.id));
  for (const n of (rfNodes || [])) {
    if (!n || !n.id) continue;
    const slugs = dispatchedSlugsOf(nodeConfigs?.[n.id]?.dispatchesWorkflow);
    for (const slug of slugs) {
      const id = dispatchMemberId(n.id, slug);
      // A real node whose id collides would be silently replaced in the id set
      // React Flow keys on, so yield to it rather than draw over it.
      if (taken.has(id)) continue;
      taken.add(id);
      nodes.push({
        id,
        type: 'workflowNode',
        draggable: false,
        data: {
          label: slug,
          // The SLUG, not a display name: the viewer has no catalog (it draws
          // deployed agents too), and the slug is what the deploy actually
          // creates — one rule, no lookup, nothing to drift from the twin.
          nodeType: 'agent',
          status: 'pending',
          description: null,
          descriptionFull: null,
          configurable: false,
          // No model disc: the child agent owns its own nodes' models, set on
          // the CHILD's page. Same rule the dispatcher itself already follows.
          runsModel: false,
          dispatchesWorkflow: [slug],
          // The box OWNS its click, even where it opens nothing. Leaving the
          // handler off does not make the click inert — GraphFlowEditor falls
          // through to the generic branch and opens the node-config modal for
          // a synthetic id that has no config: a blank "Node — Configuration"
          // panel. On the deployed-agent surface PipelineGraphView replaces
          // this with "go to that child agent"; on a marketplace preview the
          // child is not deployed anywhere, so nothing to open is the truth.
          onNodeClick: () => {},
        },
        position: { x: 0, y: 0 },
      });
      edges.push({
        id: `e${id}`,
        source: n.id,
        target: id,
        type: 'smoothstep',
        focusable: false,
        selectable: false,
        // DASHED on purpose. A solid arrow on this canvas means "the next step
        // of this pipeline", and a member is not that — it is a separate agent
        // this node STARTS. Same visual grammar as the model-disc connector:
        // "attached to", not "flows into".
        style: { stroke: 'rgba(190, 190, 190, 0.85)', strokeWidth: 1.5, strokeDasharray: '5 4' },
      });
    }
  }
  return { nodes, edges };
}

/**
 * Convert backend serialized graph to React Flow format with auto-layout
 *
 * @param {Object} backendGraph - Serialized graph from backend API
 * @returns {Object} - { nodes, edges, nodeConfigs, stateSchema }
 */
export async function convertBackendGraphToReactFlow(backendGraph) {
  if (!backendGraph || !backendGraph.nodes) {
    return { nodes: [], edges: [], nodeConfigs: {}, stateSchema: null };
  }

  // Convert nodes to React Flow format
  const nodes = backendGraph.nodes.map(nodeDef => {
    const nodeType = nodeDef.type || nodeDef.data?.nodeType || nodeDef.id;
    // Caption (explicit description, else a prompt-derived summary) shown under
    // the node name on the chart. `descFull` is the untruncated text for the
    // native hover tooltip.
    const { caption: nodeDescription, full: descFull } =
      deriveNodeCaption(backendGraph.nodeConfigs?.[nodeDef.id]);

    // START / END terminal markers — rendered as small pills (TerminalNode).
    if (nodeType === 'start' || nodeType === 'end') {
      return {
        id: nodeDef.id,
        type: 'terminal',
        data: {
          terminal: nodeType,
          label: nodeDef.data?.label || (nodeType === 'start' ? 'Start' : 'End'),
        },
        position: nodeDef.position || { x: 0, y: 0 }
      };
    }

    // Check if this is a decision node
    if (nodeType === 'decision') {
      return {
        id: nodeDef.id,
        type: 'decision',
        data: {
          conditional: true,
          // An AUTHORED router node (sentry-triage's `route` / `has_issues`)
          // keeps the name its template declared; a SYNTHESIZED `<node>__branch`
          // box (the engine materializes one after a working node that forks —
          // board-runner's `intake` / `poll`) is furniture and stays anonymous.
          // See BRANCH_NODE_LABEL / nodeBoxCaption in selfhosted/ui/index.html
          // for the same contract on the self-host dashboard.
          label: decisionNodeLabel(nodeDef),
          description: nodeDescription,
          descriptionFull: descFull
        },
        position: nodeDef.position || { x: 0, y: 0 }
      };
    }

    // Regular workflow node - label comes from backend data
    const dispatched = dispatchedSlugsOf(backendGraph.nodeConfigs?.[nodeDef.id]?.dispatchesWorkflow);
    return {
      id: nodeDef.id,
      type: 'workflowNode',
      data: {
        label: nodeDef.data?.label || nodeType,
        nodeType: nodeType,
        status: 'pending',
        description: nodeDescription,
        descriptionFull: descFull,
        configurable: !!backendGraph.nodeConfigs?.[nodeDef.id]?.prompt,
        // Does this node invoke a model? Derived from the graph alone, so the
        // layout can reserve room for its model disc before we know anything
        // about credentials. Sub-graph dispatch nodes are excluded — the child
        // agent owns its own nodes' models.
        runsModel: nodeRunsModel(backendGraph.nodeConfigs?.[nodeDef.id]),
        // The CHILD agent(s) this node dispatches, if any. Carried onto the
        // node so the graph can send a click to that agent's own page and mark
        // it when the child isn't runnable yet. NORMALIZED to a slug ARRAY (or
        // null): the serialized marker is a string OR an array of strings, and
        // node data with one shape is the only way the badge, the click target
        // and the readiness ring can agree.
        dispatchesWorkflow: dispatched.length ? dispatched : null,
      },
      position: nodeDef.position || { x: 0, y: 0 }
    };
  });

  // Convert edges to React Flow format. Drop edges that reference a node not
  // in the node set — serialized graphs include edges to virtual terminals
  // (e.g. `END`/`START`) and conditional routes that may point at ids we don't
  // render as nodes. React Flow errors on edges with a missing source/target,
  // which can blank the whole canvas, so filter them out first.
  const nodeIdSet = new Set(nodes.map((n) => n.id));
  const edges = (backendGraph.edges || [])
    .filter((e) => nodeIdSet.has(e.source) && nodeIdSet.has(e.target))
    .map((edgeDef, index) => {
    const edge = {
      id: edgeDef.id || `e${index + 1}`,
      source: edgeDef.source,
      target: edgeDef.target,
      type: 'smoothstep'
    };
    
    if (edgeDef.label) {
      edge.label = edgeDef.label;
    }
    
    // Preserve source/target handles if they exist in backend data
    if (edgeDef.sourceHandle) {
      edge.sourceHandle = edgeDef.sourceHandle;
    }
    if (edgeDef.targetHandle) {
      edge.targetHandle = edgeDef.targetHandle;
    }
    
    // Handle conditional edges. Calm styling: a thin dashed line in a muted
    // branch color, and NO marching-ants animation — on a long early-exit edge
    // (e.g. `fetch_issues → END` when there are 0 issues) the animation + bright
    // orange read as tangled noise rather than an intentional branch.
    if (edgeDef.data?.conditionalCode) {
      const isValidPath = edgeDef.label?.toLowerCase().includes('valid') &&
                          !edgeDef.label?.toLowerCase().includes('invalid');
      edge.style = {
        stroke: isValidPath ? '#5aa86f' : '#c98a4b',
        strokeWidth: 1.5,
        strokeDasharray: '6 4',
      };
      edge.animated = false;
      edge.data = {
        conditionalCode: edgeDef.data.conditionalCode,
        isConditional: true
      };
    }
    
    return edge;
  });

  // The child agents each dispatching node starts, drawn as their own boxes
  // joined by a line (see dispatchMemberNodes). Appended BEFORE layout so elk
  // places them like any other node — a fan-out gets a real fan, not a hand-
  // positioned cluster that overlaps whatever the graph does next.
  const members = dispatchMemberNodes(nodes, backendGraph.nodeConfigs);
  nodes.push(...members.nodes);
  edges.push(...members.edges);

  // Assign handles for proper connection points
  assignEdgeHandles(nodes, edges);

  // Apply auto-layout if positions are all at origin
  const needsLayout = nodes.every(n => n.position.x === 0 && n.position.y === 0);
  let layoutedNodes = nodes;
  let layoutedEdges = edges;

  if (needsLayout) {
    const hasDiscs = nodes.some((n) => n.data?.runsModel);
    const result = await getLayoutedElements(nodes, edges, {
      siblingSpacing: hasDiscs ? MODEL_DISC_SIBLING_SPACING : 0,
      layerSpacing: hasDiscs ? MODEL_DISC_LAYER_SPACING : 0,
    });
    layoutedNodes = result.nodes;
    layoutedEdges = result.edges;
  }

  return {
    nodes: layoutedNodes,
    edges: layoutedEdges,
    nodeConfigs: backendGraph.nodeConfigs || {},
    stateSchema: backendGraph.stateSchema || null
  };
}

/**
 * Assign source/target handles for proper edge rendering
 * Only assigns handles if they don't already exist (fallback for legacy data)
 */
function assignEdgeHandles(nodes, edges) {
  // Left→right flow: every edge leaves its source's RIGHT side and enters its
  // target's LEFT side. (Decision branches all leave from 'right' and fan out to
  // their differently-placed targets — smoothstep separates them.)
  edges.forEach(edge => {
    if (!edge.sourceHandle) edge.sourceHandle = 'right';
    if (!edge.targetHandle) edge.targetHandle = 'target-left';
  });
}
